import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@mui/material';
import { isEqual } from 'lodash';
import toaster from 'react-hot-toast';
import { EnvironmentalVariables } from 'components/common/environmentalVariables/EnvironmentalVariables';
import { updateLocationEnvVars } from 'api/admin/washboard/washboard';

interface StoreEnvVariablesProps {
  envVariables?: Record<string, string>;
  storeId: number;
  onUpdateSuccess?: () => void;
}

export const StoreEnvVariables: React.FC<StoreEnvVariablesProps> = ({
  envVariables: initialEnvVariables,
  storeId,
  onUpdateSuccess,
}) => {
  const [envVariables, setEnvVariables] = useState<Record<string, string>>(
    initialEnvVariables || {},
  );
  const [hasEnvVarErrors, setHasEnvVarErrors] = useState(false);
  const [hasEnvChanges, setHasEnvChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setEnvVariables(initialEnvVariables || {});
  }, [initialEnvVariables]);

  const hasChanges = useMemo(() => {
    const originalEnvVars = initialEnvVariables || {};
    return !isEqual(envVariables, originalEnvVars) || hasEnvChanges;
  }, [envVariables, hasEnvChanges, initialEnvVariables]);

  const isSaveDisabled = useMemo(
    () => isSaving || hasEnvVarErrors || !hasChanges,
    [isSaving, hasEnvVarErrors, hasChanges],
  );

  const handleCancel = () => {
    setEnvVariables(initialEnvVariables || {});
  };

  const handleEnvVariablesChange = (
    newEnvVars: Record<string, string>,
    hasErrors: boolean,
    hasChanges: boolean,
  ) => {
    setEnvVariables(newEnvVars);
    setHasEnvVarErrors(hasErrors);
    setHasEnvChanges(hasChanges);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateLocationEnvVars(storeId, { envVariables });
      toaster.success('Store device configuration updated successfully');
      onUpdateSuccess?.();
    } catch (error: any) {
      toaster.error(error?.response?.data?.error || 'Failed to update store device configuration');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="env-vars-section">
      <EnvironmentalVariables envVariables={envVariables} onChange={handleEnvVariablesChange} />
      {hasChanges && (
        <div className="configuration-actions">
          <Button variant="outlined" color="primary" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isSaveDisabled}
          >
            Save Changes
          </Button>
        </div>
      )}
    </div>
  );
};
